/**
 * ChatGPT Integration Instructions Page
 * Provides step-by-step instructions for integrating Complin with ChatGPT
 */

import React from 'react';
import { ArrowLeft, ExternalLink, CheckCircle, AlertCircle, Settings, Plus, Link as LinkIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

const ChatGPTIntegrationInstructions: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const getMcpUrl = (apiKey: string): string => {
    const isLocal = typeof window !== 'undefined' && (
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1'
    );
    
    const baseUrl = isLocal
      ? 'http://localhost:5001/lemur-476302/us-central1/mcp'
      : 'https://us-central1-lemur-476302.cloudfunctions.net/mcp';
    
    return `${baseUrl}?api_key=${encodeURIComponent(apiKey)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/settings')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Settings
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            ChatGPT Integration Instructions
          </h1>
          <p className="text-gray-600">
            Follow these steps to connect Complin with ChatGPT using MCP (Model Context Protocol)
          </p>
        </div>

        {/* Prerequisites */}
        <div className="card mb-6 border-l-4 border-blue-500">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Prerequisites</h2>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>ChatGPT Plus subscription or higher</strong> - You need at least a Plus subscription to use MCP integrations</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>An API key from Complin</strong> - Create one in Settings → API Keys if you haven't already</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Step-by-Step Instructions */}
        <div className="space-y-6">
          {/* Step 1 */}
          <div className="card">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg">
                1
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Enable Development Mode
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">1.1</span>
                    <span>Open ChatGPT and click on your profile icon (bottom left)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">1.2</span>
                    <span>Go to <strong>Settings</strong></span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">1.3</span>
                    <span>Navigate to <strong>Advanced Settings</strong> (or "Beta features" depending on your ChatGPT version)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">1.4</span>
                    <span>Enable <strong>Development Mode</strong> (toggle it on)</span>
                  </li>
                </ol>
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Development Mode allows you to connect custom MCP servers. This feature may be in different locations depending on your ChatGPT version.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Step 2 */}
          <div className="card">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg">
                2
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Create a New App
                </h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">2.1</span>
                    <span>In ChatGPT settings, go to <strong>Apps & Connectors</strong> (or "Connections" / "Integrations")</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">2.2</span>
                    <span>Click on <strong>Create App</strong> or <strong>Add Connection</strong></span>
                  </li>
                </ol>
              </div>
            </div>
          </div>

          {/* Step 3 */}
          <div className="card">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg">
                3
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center gap-2">
                  <LinkIcon className="w-5 h-5" />
                  Configure the App
                </h3>
                <p className="text-gray-700 mb-4">Fill in the following details:</p>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Name</label>
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-2 bg-white border border-gray-300 rounded flex-1 font-mono">Complin</code>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                    <div className="flex items-center gap-2">
                      <code className="px-3 py-2 bg-white border border-gray-300 rounded flex-1">Your compliance complete</code>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">MCP Server URL</label>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600 mb-2">
                        Use the MCP URL from your API key. You can copy it when creating a new key, or construct it manually:
                      </p>
                      <div className="flex items-center gap-2">
                        <code className="px-3 py-2 bg-white border border-gray-300 rounded flex-1 font-mono text-xs break-all">
                          https://us-central1-lemur-476302.cloudfunctions.net/mcp?api_key=YOUR_API_KEY
                        </code>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Replace <code className="bg-gray-200 px-1 rounded">YOUR_API_KEY</code> with your actual API key from Complin
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Authentication</label>
                    <div className="flex items-center gap-2">
                      <select className="px-3 py-2 bg-white border border-gray-300 rounded flex-1" defaultValue="no-auth">
                        <option value="no-auth">No Auth</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Select <strong>No Auth</strong> - authentication is handled via the API key in the URL
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Step 4 */}
          <div className="card">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-bold text-lg">
                4
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Complete the Setup</h3>
                <ol className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">4.1</span>
                    <span>Review all the information you've entered</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">4.2</span>
                    <span>Click on <strong>"I understand"</strong> or the acknowledgment checkbox (if present)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-gray-900">4.3</span>
                    <span>Click <strong>Submit</strong> or <strong>Create</strong> to finalize the connection</span>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Success Message */}
        <div className="card mt-6 bg-green-50 border-l-4 border-green-500">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-semibold text-green-900 mb-2">You're All Set!</h3>
              <p className="text-green-800">
                Once connected, you can use Complin's compliance features directly in ChatGPT. Try asking ChatGPT questions about your compliance data, tasks, and more!
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-6 flex gap-4">
          <button
            onClick={() => navigate('/settings')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to API Keys Settings
          </button>
          <a
            href="https://chat.openai.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors flex items-center gap-2"
          >
            Open ChatGPT
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        {/* Troubleshooting */}
        <div className="card mt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Troubleshooting</h3>
          <div className="space-y-3 text-gray-700">
            <div>
              <strong className="text-gray-900">Can't find Development Mode?</strong>
              <p className="text-sm mt-1">Make sure you have ChatGPT Plus or higher. The feature may be in "Beta features" or "Advanced settings" depending on your version.</p>
            </div>
            <div>
              <strong className="text-gray-900">Connection not working?</strong>
              <p className="text-sm mt-1">Verify your API key is correct and not revoked. Check that the MCP Server URL includes the full API key parameter.</p>
            </div>
            <div>
              <strong className="text-gray-900">Need a new API key?</strong>
              <p className="text-sm mt-1">Go to Settings → API Keys and create a new key. Remember to copy the MCP URL when creating the key!</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChatGPTIntegrationInstructions;

